import { UseQueryOptions } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { deserialize } from 'serializr';

import AIModel from 'types/ai_model';
import APIError from 'types/api_error';
import { CursorPaginated, CursorPaginationParams } from 'types/pagination';

import { useAPI } from 'hooks/api/useAPI';
import { usePlanCapability } from 'organizations/hooks/usePlanCapability';

import { useAuthenticatedQuery } from './useAuthenticatedQuery';

export type UseMarketplaceModelsParams = {
  only_included_in_plan?: boolean;
};

export const DEFAULT_MARKETPLACE_MODELS_PARAMS: UseMarketplaceModelsParams = {
  only_included_in_plan: true,
};

export function marketplaceModelsQueryKey(
  applicationID: string | undefined,
  params: UseMarketplaceModelsParams = DEFAULT_MARKETPLACE_MODELS_PARAMS
) {
  return [
    'marketplace-models',
    applicationID,
    { ...DEFAULT_MARKETPLACE_MODELS_PARAMS, ...params },
  ] as const;
}

type UseMarketplaceModelsOptions = Omit<
  UseQueryOptions<AIModel[], APIError>,
  'select'
> & {
  params?: UseMarketplaceModelsParams;
};

export function useModels(options?: UseQueryOptions<AIModel[], APIError>) {
  const api = useAPI();
  const { isEnabled: canCreateCustomModels, isLoading: isPlanLoading } =
    usePlanCapability('can_create_custom_models');
  const enabled =
    (options?.enabled ?? true) && !isPlanLoading && canCreateCustomModels;

  const query = useAuthenticatedQuery<AIModel[]>(
    ['models', api.applicationID],
    api.models.list,
    { ...options, staleTime: Infinity, enabled }
  );

  return { ...query, isLoading: query.isInitialLoading };
}

export function useMarketplaceModels({
  params,
  ...options
}: UseMarketplaceModelsOptions = {}) {
  const api = useAPI();

  const queryParams = {
    ...DEFAULT_MARKETPLACE_MODELS_PARAMS,
    ...params,
  };

  return useAuthenticatedQuery<AIModel[]>(
    marketplaceModelsQueryKey(api.applicationID, queryParams),
    () => listMarketplaceModels(api.client, api.applicationID, queryParams),
    {
      ...options,
      select: (marketplaceModels) =>
        marketplaceModels.map((model) => {
          model.source = 'marketplace';
          return model;
        }),
      staleTime: Infinity,
    }
  );
}

async function listMarketplaceModels(
  client: AxiosInstance,
  applicationID: string | undefined,
  params: UseMarketplaceModelsParams
) {
  if (!applicationID) {
    throw {
      code: 'missing-route-parameters',
      status: 400,
      message: 'Missing application route parameter',
    } satisfies APIError;
  }

  const models: AIModel[] = [];
  let nextCursor: string | null = null;

  do {
    const queryParams: UseMarketplaceModelsParams & CursorPaginationParams = {
      ...params,
      pagination: 'cursor',
      limit: 150,
    };

    if (nextCursor) {
      queryParams.next_cursor = nextCursor;
    }

    const { data } = await client.get<CursorPaginated<AIModel>>(
      `/internal/apps/${applicationID}/marketplace/models`,
      { params: queryParams }
    );

    models.push(...(deserialize(AIModel, data.data) as AIModel[]));
    nextCursor = data.next_cursor;
  } while (nextCursor !== null);

  return models;
}

export function useAdminMarketplaceModels(
  options?: Omit<UseQueryOptions<AIModel[], APIError>, 'select'>
) {
  const api = useAPI();

  return useAuthenticatedQuery<AIModel[]>(
    ['admin-marketplace-models'],
    api.models.marketplace.admin.list,
    {
      ...options,
      select: (marketplaceModels) =>
        marketplaceModels.map((model) => {
          model.source = 'marketplace';
          return model;
        }),
      staleTime: Infinity,
    }
  );
}
